import type { ChannelSlice } from '../ChannelSlice';
import type { ColumnOptionsSlice } from '../ColumnOptionsSlice';
import type { FastIdeasSelector } from '../FastIdeasSelector';
import type { ResultsAreaSlice } from '../ResultsAreaSlice';
import type { SlowIdeasSelector } from '../SlowIdeasSelector';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { Instant } from '@js-joda/core';
import { signal, computed, effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    ConcreteIdea,
    Idea,
    IfThenGroup,
    IfThenResearchPlan,
} from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { t } from 'i18next';
import { optional } from 'inversify';
import debounce from 'lodash.debounce';
import type { TableStatus } from 'src/components/table-view/model';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import { IndicatorImportRefModel } from 'src/dtos/IndicatorImportRef';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { ConditionGroupModel } from 'src/features/searchAlpha/dtos/ConditionGroupModel';
import { conditionGroupModelFromIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelFromIfThenGroup';
import { conditionGroupModelToIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelToIfThenGroup';
import { ConditionModel } from 'src/features/searchAlpha/dtos/ConditionModel';
import { CONDITION_CHANGE_EVENT } from 'src/features/searchAlpha/types';
import { getValidationColumnDisplayName } from 'src/features/table/lib/validationColumns';
import { scrollingFastIdeaDwellTimeConfig } from 'src/lib/featureFlags/flags/scrollingFastIdeaDwellTimeConfig';
import { useWidgetTabIdForUserViewKey } from 'src/lib/featureFlags/flags/useWidgetTabIdForUserViewKey';
import type { SearchAlphaWidgetModel } from 'src/models/SearchAlphaWidgetModel';
import type { TableConstructionProgress } from 'src/models/TableModel';
import { makeHeaderElements } from 'src/models/WidgetModel';
import type { WidgetHeaderElement } from 'src/models/WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS, STANDALONE_COMMON_MENU_HEADER_ELEMENTS } from 'src/models/WidgetModel/impl';
import { getIndicatorFromRefQuery } from 'src/queries/dictionary';
// eslint-disable-next-line no-restricted-imports -- legacy direct import
import { createIdeaWithMappedImports, putIdea } from 'src/queries/http/ideas';
import { getIdeaByIdQuery, invalidateIdeaRecordsQueries } from 'src/queries/ideas';
import { getTotalRowsCountQuery } from 'src/queries/table';
import { getWorkspaceConfigQuery } from 'src/queries/workspace';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import { createDefaultSearchAlphaWidget } from 'src/store/defaults/widget';
import type { SearchAlphaWidgetViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

interface SearchAlphaWidgetModelState {
    rootGroup: ConditionGroupModel;
}

@injectable()
export class SearchAlphaWidgetModelImpl extends ReactiveInjectable implements SearchAlphaWidgetModel {
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #fastIdeasSelector: FastIdeasSelector;
    #slowIdeasSelector: SlowIdeasSelector;
    #stickyFilterSlice: StickyFilterSlice;
    #columnOptionsSlice: ColumnOptionsSlice;

    #state: SearchAlphaWidgetModelState;

    // Flag to track if we're in standalone mode (no tab in store)
    #isStandaloneMode = false;

    #sectionVisibility = signal<{ ideas: boolean; search: boolean; results: boolean }>({
        ideas: true,
        search: true,
        results: true,
    });

    // Column template state signals
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);

    // Validation and scrolling state signals
    #isValidationModeEnabled: Signal<boolean> = signal(false);
    #isScrollingEnabled: Signal<boolean> = signal(false);

    #widgetName: Signal<string> = signal('');

    #connectionStatus: Signal<{
        status: TableStatus;
        progress: TableConstructionProgress;
        failureCount: number;
    }> = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    #tableRowCount: Signal<number> = signal(0);
    #rowCountTotalSignal: Signal<number> = signal(0);
    #refetchHandler: (() => void) | null = null;

    #dwellTimeSeconds: Signal<number> = signal(0);
    #userViewKey: Signal<string | undefined> = signal(undefined);
    #lastSubmit: Signal<Instant> = signal(Instant.now());
    #hasUnsavedChanges: Signal<boolean> = signal(false);
    #canSave: Signal<boolean> = signal(false);
    #isTemplate: Signal<boolean> = signal(false);

    headerElements!: WidgetHeaderElement[];

    resultsAreaHeaderElements: WidgetHeaderElement[] = makeHeaderElements<SearchAlphaWidgetModel['eventBus']>([
        { type: 'overflowDropdown', position: 0, alwaysVisible: true },
        { type: 'scrollingTableIndicator', position: 0.5, alwaysVisible: true },
        { type: 'stickyFilter', position: 1, priority: 80 },
        { type: 'spacer', position: 3, alwaysVisible: true },
        {
            key: 'ideasMenu-slow',
            type: 'ideasMenu',
            position: 5,
            priority: 70,
            ideaType: 'slow',
            ideaPlanTypes: ['watchlist', 'screener', 'universe'],
            selectType: 'single',
            showSelected: false,
            allowDelete: true,
        },
        { type: 'columnTemplateMenu', position: 6, priority: 10, columnTemplateKey: 'ticker' },
        { type: 'settingsMenu', position: 7, priority: 60 },
        {
            type: 'tableRowCountWithTotal',
            position: 9,
            priority: 30,
        },
        { type: 'connectionStatus', position: 10, priority: 40 },
        { type: 'exportCSVButton', position: 11, priority: 20 },
    ]);

    get children() {
        return this.#state.rootGroup.children as ConditionGroupModel[];
    }

    constructor(
        @inject('ImportsManagerModel') @reacts public importsManager: ReactBindings['ImportsManagerModel'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('SearchAlphaWidgetAdapter')
        private adapter: ReactBindings['SearchAlphaWidgetAdapter'],
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('AnalyticsService') private analyticsService: ReactBindings['AnalyticsService'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('FastIdeasSelector') fastIdeasSelector: FastIdeasSelector,
        @inject('SlowIdeasSelector') slowIdeasSelector: SlowIdeasSelector,
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('StickyFilterSlice') stickyFilterSlice: ReactBindings['StickyFilterSlice'],
        @inject('ResultsAreaSlice') private resultsAreaSlice: ResultsAreaSlice,
        @inject('ColumnOptionsSlice') columnOptionsSlice: ReactBindings['ColumnOptionsSlice'],
        @inject('TableModel') private totalTableModel: ReactBindings['TableModel'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#log = logger.getSubLogger({ name: 'SearchAlphaWidgetModel' });

        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#fastIdeasSelector = fastIdeasSelector;
        this.#slowIdeasSelector = slowIdeasSelector;
        this.#stickyFilterSlice = stickyFilterSlice;
        this.#columnOptionsSlice = columnOptionsSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.#state = {
            rootGroup: new ConditionGroupModel({
                booleanOperator: 'and',
                children: [],
                id: v4(),
                isSelfEnabled: true,
                isExpanded: true,
                label: undefined,
                parent: false,
                backtestMode: this.adapter.backtestMode,
                defaultNewConditionMode: this.adapter.defaultNewConditionMode,
            }),
        };

        // TODO implement a change target subscription class
        const listener = this.#onChildChange.bind(this);
        this.#state.rootGroup.changeTarget.addEventListener(CONDITION_CHANGE_EVENT, listener);
        this.disposableStack.defer(() => {
            this.#state.rootGroup.changeTarget.removeEventListener(CONDITION_CHANGE_EVENT, listener);
        });

        this.eventBus = this.#buildEventBus();
    }

    get canSave() {
        const strategyRecord = this.adapter.getState().strategy;
        if (!strategyRecord) {
            return false;
        }

        return Boolean(strategyRecord.name) && Boolean(strategyRecord.id) && this.hasUnsavedChanges;
    }

    /**
     * Determine if the user is allowed to take a "submit" action
     */
    get canSubmit() {
        if (this.backtestMode) {
            return false;
        }

        // Is there something preventing submission?
        if (this.#isSubmitBlocked) {
            return false;
        }

        // Is any field dirty?
        for (const child of this.#iterateChildren(this.#state.rootGroup)) {
            if (child.isDirty) {
                return true;
            }
        }

        return false;
    }

    get backtestMode() {
        return this.adapter.backtestMode;
    }

    get defaultNewConditionMode() {
        return this.adapter.defaultNewConditionMode;
    }

    #childBeingDragged: string | undefined;

    get childBeingDragged() {
        return this.#childBeingDragged;
    }

    set childBeingDragged(value: string | undefined) {
        this.#childBeingDragged = value;
        const conditionToMove = this.rootGroup.findChildById(value ?? '');
        this.rootGroup.processInFlightChild(conditionToMove as any);
    }

    /**
     * Creates a new strategy and swaps the contents of the widget model to the new strategy
     */
    async createNewStrategy(): Promise<Idea & { plan: IfThenResearchPlan }> {
        const newStrategy = createDefaultSearchAlphaWidget().idea;

        for (const child of this.#state.rootGroup.children) {
            this.#state.rootGroup.removeChild(child);
        }
        // This is safe to cast because it has no imports!
        this.adapter.persist({ strategy: newStrategy as Idea & { plan: IfThenResearchPlan }, isIdeaDirty: false });
        this.#widgetName.value = t('widgets::searchalpha::name');

        // Note: this is duplicated from SearchAlphaWidgetModel.init
        {
            conditionGroupModelFromIfThenGroup(
                (newStrategy.plan as IfThenResearchPlan).root,
                this.#state.rootGroup,
                true,
            );

            if (this.#state.rootGroup.children.length === 0) {
                // default widgets with no strategy to have one conditionGroup
                this.#state.rootGroup.addNewConditionGroup();
            }

            for (const child of this.#iterateChildren(this.#state.rootGroup)) {
                child.setIsDirty(false);
                child.setIsUnsaved(false);
            }
            this.#syncIdeaEditorSignals();
        }

        this.#lastSubmit.value = Instant.now();

        return newStrategy;
    }

    get hasUnsavedChanges() {
        for (const child of this.#iterateChildren(this.#state.rootGroup)) {
            if (child.isUnsaved) {
                return true;
            }
        }
        return false;
    }

    eventBus!: SearchAlphaWidgetModel['eventBus'];

    #buildEventBus(): SearchAlphaWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Section view events
                .addEvent('widget::toggleSectionVisibility', (payload) => {
                    if (payload.sectionId === 'ideas') {
                        this.isShowingIdeas = payload.show;
                    } else if (payload.sectionId === 'search') {
                        this.isShowingSearch = payload.show;
                    } else if (payload.sectionId === 'results') {
                        this.isShowingResults = payload.show;
                    }
                    this.#sectionVisibility.value = {
                        ideas: this.isShowingIdeas,
                        search: this.isShowingSearch,
                        results: this.isShowingResults,
                    };
                })
                // Indicator library event
                .addEvent('widget::onIndicatorSelected', (payload) => {
                    this.#handleIndicatorSelected(payload.indicator);
                })
                // Idea editor action events (IPC-safe, no callbacks)
                .addEvent('widget::ideaEditorSave', async () => {
                    await this.saveStrategy();
                })
                .addEvent('widget::ideaEditorSaveAs', async (payload) => {
                    await this.saveStrategyAs(payload.name);
                })
                .addEvent('widget::ideaEditorCreateNew', () => {
                    this.createNewStrategy();
                })
                // Column template events
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.#updateWidgetState({ columnTemplate: payload.templateId });
                    this.#selectedColumnTemplate.value = payload.templateId;
                })

                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.#updateWidgetState({ columnTemplate: payload.columnTemplate });
                    this.#selectedColumnTemplate.value = payload.columnTemplate;
                })
                // Column template library event
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                // Validation and scrolling events
                .addEvent('widget::toggleValidationMode', () => {
                    this.#isValidationModeEnabled.value = !this.#isValidationModeEnabled.value;
                })
                .addEvent('widget::toggleScrolling', () => {
                    const next = !this.#isScrollingEnabled.value;
                    if (this.#isStandaloneMode) {
                        this.#isScrollingEnabled.value = next;
                        this.adapter.persist({ scrolling: next });
                        return;
                    }
                    // Only update Redux - the subscribeScrolling subscription will update the local signal
                    this.#updateWidgetState({ scrolling: next });
                })
                // Export CSV event
                .addEvent('widget::exportCSV', () => {
                    void this.#exportCSV();
                })
                // Ideas menu signals - required by IdeasSignals interface
                .addSignal('widget::slowIdea', () => computed(() => null as IdeaDTO<IdeaPlanDTO> | null))
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => [] as { type: 'idea' | 'universe'; id: string }[]),
                )
                // Idea editor capability signals (enables save/saveAs/createNew in ideas menu)
                .addSignal('widget::ideaEditorIsDirty', () => this.#hasUnsavedChanges)
                .addSignal('widget::ideaEditorCanSave', () => this.#canSave)
                .addSignal('widget::ideaEditorIsTemplate', () => this.#isTemplate)
                .addSignal('widget::ideaEditorUnsavedChangesLabel', () =>
                    computed(() => t('widgets::searchalpha::unsaved-changes')),
                )
                .addSignal('widget::sectionVisibility', () => this.#sectionVisibility)
                // Column template signals
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate || typeof columnTemplate === 'string') {
                            // Return null settings when no column template is set yet or it's a reference
                            return { settings: null, defaults: null };
                        }
                        // The column template IS the grid settings for SearchAlpha widget
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                // Validation and scrolling signals
                .addSignal('widget::isValidationModeEnabled', () => this.#isValidationModeEnabled)
                .addSignal('widget::isScrollingEnabled', () => this.#isScrollingEnabled)
                // Table row count signals
                .addSignal('widget::tableRowCount', () => this.#tableRowCount)
                .addSignal('widget::tableTotalRowCount', () => this.#rowCountTotalSignal)
                // Connection status signal
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.#refetchHandler?.();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#fastIdeasSelector.eventBus)
            .merge(this.#slowIdeasSelector.eventBus)
            .merge(this.#stickyFilterSlice.eventBus)
            .merge(this.#columnOptionsSlice.eventBus)
            .merge(this.resultsAreaSlice.eventBus)
            .merge(
                // Override widget::name to show idea name instead of tab name
                EventBusBuilder.create()
                    .addSignal('widget::name', () => this.#widgetName)
                    .build(),
            );
    }

    async init(tabId: string) {
        // Check if the tab exists in the store (standalone mode uses a fake tabId)
        const initialState = this.store.getState();
        const tabExists = initialState.tab.byId[tabId] !== undefined;
        this.#isStandaloneMode = !tabExists;

        // Temporary: In standalone mode, use simple "Results: X" instead of "Results: X out of Y"
        // because the total row count is not accurate in standalone mode
        if (this.#isStandaloneMode) {
            this.resultsAreaHeaderElements = this.resultsAreaHeaderElements.map((element) =>
                element.type === 'tableRowCountWithTotal' ? { ...element, type: 'tableRowCount' as const } : element,
            );
        }

        const baseElements = !this.#isStandaloneMode
            ? COMMON_MENU_HEADER_ELEMENTS
            : STANDALONE_COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = makeHeaderElements<SearchAlphaWidgetModel['eventBus']>([
            ...baseElements,
            {
                type: 'ideasMenuItems',
                position: 2,
                overflowOnly: true,
                items: ['new', 'open', 'save', 'copy', 'delete'],
                ideaType: 'fast',
                ideaPlanTypes: ['if-then'],
                selectType: 'single',
                showSelected: false,
                allowDelete: true,
            },
            { type: 'helpDocument', documentId: 'searchalpha-overview', position: 39, priority: 5 },
            { type: 'indicatorLibrary', position: 40, priority: 7 },
            {
                type: 'sectionViewMenu',
                position: 80,
                priority: 6,
                viewConfig: [
                    { id: 'ideas', label: 'Ideas' },
                    { id: 'search', label: 'Search' },
                    { id: 'results', label: 'Results' },
                ],
            },
        ]);

        // Initialize slices with shared adapter (works for both workspace and standalone)
        this.#stickyFilterSlice.init(this.adapter);
        this.#columnOptionsSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);
        this.disposableStack.use(this.#columnOptionsSlice);

        this.#fastIdeasSelector.init(this.adapter, {
            get: (state) => (state.strategy?.id ? [state.strategy.id] : []),
            set: (_ideaIds) => ({}), // SearchAlpha persists the full strategy via adapter, not just the ID
            onIdeasSelected: async (payload) => {
                const idea = payload.ideas[0];
                // IdeaDTO has 'idea' property, universe selection has 'universeId'
                if (idea && !('universeId' in idea)) {
                    const ideaDto = idea as IdeaDTO<IdeaPlanDTO>;
                    if (ideaDto.idea.id) {
                        await this.useStrategyById(ideaDto.idea.id);
                    }
                }
            },
        });

        this.#slowIdeasSelector.init(this.adapter, {
            onSlowIdeaSelected: (payload) => {
                const idea = payload.ideas[0];
                if (!idea) {
                    this.adapter.persist({ universeId: null });
                } else if ('universeId' in idea) {
                    this.adapter.persist({ universeId: idea.universeId });
                } else {
                    this.adapter.persist({ universeId: (idea as IdeaDTO<IdeaPlanDTO>).idea.id ?? null });
                }
            },
        });
        this.disposableStack.use(this.#slowIdeasSelector);

        // Establish the user view key before ResultsAreaSlice constructs its table query.
        const uniqueMountId = v4();
        const userViewKeyUnsubscribe = this.store
            .subscribeSelector((state) => useWidgetTabIdForUserViewKey.select(state), { leading: true })
            .handle((useTabId) => {
                this.#userViewKey.value = useTabId && tabId !== undefined ? tabId : uniqueMountId;
            });
        this.disposableStack.defer(userViewKeyUnsubscribe);

        // Initialize ResultsAreaSlice
        this.resultsAreaSlice.init(this.adapter, {
            columnTemplateKey: 'ticker',
            tabId: this.#isStandaloneMode ? undefined : tabId,
            ideaMode: 'standard',
            ideaUserViewKeyScope: 'fast',
            fastIdeasSelector: this.#fastIdeasSelector,
            slowIdeasSelector: this.#slowIdeasSelector,
            validationModeSignal: this.#isValidationModeEnabled,
            dwellTimeSignal: this.#dwellTimeSeconds,
            userViewKeySignal: this.#userViewKey,
            onUserSelectedSymbol: (symbol) => this.adapter.userSelectedSymbol(symbol),
            stickyFilterEventBus: this.#stickyFilterSlice.eventBus,
            columnOptionsEventBus: this.#columnOptionsSlice.eventBus,
            devtoolsName: 'SearchAlpha/results',
        });
        this.disposableStack.use(this.resultsAreaSlice);

        // Sync resolved column template from slice to local signal
        this.disposableStack.defer(
            effect(() => {
                const loadedTemplate = this.resultsAreaSlice.columnTemplate.value;
                if (loadedTemplate && this.#selectedColumnTemplate.value !== loadedTemplate) {
                    this.#selectedColumnTemplate.value = loadedTemplate;
                }
            }),
        );

        // Wire table connection status and row count from slice
        this.disposableStack.defer(
            effect(() => {
                const table = this.resultsAreaSlice.table;
                this.#connectionStatus.value = {
                    status: table.connectionStatus.value,
                    progress: table.progress.value,
                    failureCount: table.failureCount.value,
                };
            }),
        );
        this.disposableStack.use(
            this.resultsAreaSlice.table.count$.subscribe((count) => {
                this.#tableRowCount.value = count;
            }),
        );

        this.#refetchHandler = () => this.resultsAreaSlice.table.refetch();

        // Initialize total row count
        this.#initTotalCount();

        // Wire fast ideas from strategy to ResultsAreaSlice
        // The fast idea is the if-then strategy plan, re-created whenever lastSubmit changes
        this.disposableStack.defer(
            effect(() => {
                // Read the signal to subscribe to changes
                void this.#lastSubmit.value;
                untracked(() => this.#updateFastIdeasOnSlice());
            }),
        );

        this.#sectionVisibility.value = {
            ideas: this.adapter.getState().isShowingIdeas,
            search: this.adapter.getState().isShowingSearch,
            results: this.adapter.getState().isShowingResults,
        };

        if (!this.#isStandaloneMode) {
            // Normal widget mode - tab exists in store
            this.widgetData.init(tabId);

            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);

            // Listen for section visibility updates
            const unsubscribe = this.store
                .subscribeSelector((state) => {
                    const tabStillExists = state.tab.byId[tabId] !== undefined;
                    if (!tabStillExists) {
                        return null;
                    }
                    const adapterState = this.adapter.getState();
                    return {
                        isShowingIdeas: adapterState.isShowingIdeas,
                        isShowingSearch: adapterState.isShowingSearch,
                        isShowingResults: adapterState.isShowingResults,
                    };
                })
                .handle((data) => {
                    if (data !== null) {
                        this.#sectionVisibility.value = {
                            ideas: data.isShowingIdeas,
                            search: data.isShowingSearch,
                            results: data.isShowingResults,
                        };
                    }
                });

            this.disposableStack.defer(() => {
                unsubscribe();
            });
        }

        // Subscribe to dwell time feature flag
        const dwellTimeUnsubscribe = this.store
            .subscribeSelector((state) => scrollingFastIdeaDwellTimeConfig.select(state))
            .handle((config) => {
                this.#dwellTimeSeconds.value = config.scrollingDwellTimeSeconds;
            });
        this.disposableStack.defer(dwellTimeUnsubscribe);

        // Initialize scrolling and column template signals from adapter state
        this.#isScrollingEnabled.value = this.adapter.getState().scrolling;
        this.#selectedColumnTemplate.value = this.adapter.getState().columnTemplate;

        const strategy = this.adapter.getState().strategy;

        // Initialize the strategy into the group
        await this.importsManager.addImportsFromRefs(
            strategy.plan.imports.map((importRef) => IndicatorImportRefModel.fromIndicatorImportRef(importRef)),
        );

        // Note: this is duplicated to SearchAlphaWidgetModel.createNewStrategy
        {
            conditionGroupModelFromIfThenGroup((strategy.plan as IfThenResearchPlan).root, this.#state.rootGroup, true);
            if (strategy.name) {
                this.rootGroup.setLabel(strategy.name);
            }

            if (this.#state.rootGroup.children.length === 0) {
                // default widgets with no strategy to have one conditionGroup
                this.#state.rootGroup.addNewConditionGroup();
            }

            for (const child of this.#iterateChildren(this.#state.rootGroup)) {
                child.setIsDirty(false);
                child.setIsUnsaved(false);
            }
            this.#syncIdeaEditorSignals();
        }

        this.#widgetName.value =
            strategy.name ||
            (!this.#isStandaloneMode
                ? this.#widgetTabSlice.eventBus.getSignal('widget::name').value
                : t('widgets::searchalpha::name'));

        // do all setup before subscribing change events
        this.disposableStack.defer(this.adapter.subscribe(() => this.#onAdapterChange()));

        if (!this.#isSubmitBlocked) {
            await this.submit();
        }
    }

    /**
     * isDirty is a computed property that returns true if any of the conditions in the search have been modified from last submission
     */
    get isDirty() {
        for (const child of this.#iterateChildren(this.#state.rootGroup)) {
            if (child.isDirty) {
                return true;
            }
        }
        return false;
    }

    get isShowingIdeas() {
        return this.adapter.getState().isShowingIdeas;
    }

    set isShowingIdeas(value: boolean) {
        this.adapter.persist({ isShowingIdeas: value });
    }

    get isShowingSearch() {
        return this.adapter.getState().isShowingSearch;
    }

    set isShowingSearch(value: boolean) {
        this.adapter.persist({ isShowingSearch: value });
    }

    get isShowingResults() {
        return this.adapter.getState().isShowingResults;
    }

    set isShowingResults(value: boolean) {
        this.adapter.persist({ isShowingResults: value });
    }

    /**
     * Is there an issue that would prevent results from being returned?
     *
     * This has an impact on auto-submits separately from dirty search submissions
     */
    get #isSubmitBlocked() {
        for (const child of this.#iterateChildren(this.#state.rootGroup)) {
            if (child instanceof ConditionModel && child.errors !== null && child.isEnabledInSearch) {
                return true;
            }
            if (child instanceof ConditionModel && child.isPendingNlp) {
                return true;
            }
        }
        return false;
    }

    get isTemplate() {
        return this.adapter.getState().strategy.isTemplate;
    }

    *#iterateChildren(group: ConditionGroupModel): Generator<ConditionGroupModel | ConditionModel> {
        for (const child of group.children) {
            yield child;
            if (child instanceof ConditionGroupModel) {
                yield* this.#iterateChildren(child);
            }
        }
    }

    *#iterateSubmissionBlockers(): Generator<{ model: ConditionModel; isError: boolean; isPendingNlp: boolean }> {
        for (const child of this.#iterateChildren(this.#state.rootGroup)) {
            const isError = child instanceof ConditionModel && child.errors !== null && child.isEnabledInSearch;
            const isPendingNlp = child instanceof ConditionModel && child.isPendingNlp;
            if (isError || isPendingNlp) {
                yield { model: child, isError, isPendingNlp };
            }
        }
    }

    #log: ReactBindings['Logger'];

    #markAsSaved() {
        for (const child of this.#iterateChildren(this.#state.rootGroup)) {
            child.setIsUnsaved(false);
        }
        this.#syncIdeaEditorSignals();
    }

    #syncIdeaEditorSignals() {
        this.#hasUnsavedChanges.value = this.hasUnsavedChanges;
        this.#canSave.value = this.canSave;
        this.#isTemplate.value = this.isTemplate;
    }

    #updateWidgetState(updates: Partial<SearchAlphaWidgetViewModel>) {
        if (this.#isStandaloneMode) {
            this.adapter.persist(updates);
            return;
        }
        this.store.dispatch(
            updateWidget({
                tabId: this.widgetData.tabId,
                widgetUpdates: updates,
            }),
        );
    }

    #handleIndicatorSelected(indicator: ConcreteIndicator) {
        const groups = this.rootGroup.children.filter(
            (child): child is ConditionGroupModel => child instanceof ConditionGroupModel,
        );
        let targetGroup = groups.length > 0 ? groups[groups.length - 1] : null;

        if (!targetGroup) {
            // Create a new group if none exists
            targetGroup = new ConditionGroupModel({
                isExpanded: true,
                booleanOperator: 'and',
                isSelfEnabled: true,
                label: '',
                id: v4(),
                children: [],
                parent: this.rootGroup,
            });
            this.rootGroup.addChild(targetGroup);
        }

        // Check if last child in group is empty, if so remove it after adding new condition
        const lastChild =
            targetGroup.children.length > 0 ? targetGroup.children[targetGroup.children.length - 1] : null;

        // Create a new condition in formula mode
        const code = `${indicator.symbol}()`;
        const newCondition = new ConditionModel({
            id: v4(),
            isSelfEnabled: true,
            parent: targetGroup,
            conditionState: {
                type: 'non-guided',
                state: {
                    naturalLanguage: '',
                    displayMode: 'formula',
                    atom: {
                        status: 'user_code',
                        code,
                    },
                },
            },
            autoFocus: true,
            initialCursorOffset: indicator.symbol.length + 1,
        });

        targetGroup.addChild(newCondition);

        // Remove empty last child if it existed
        if (lastChild && lastChild instanceof ConditionModel && lastChild.isEmpty) {
            targetGroup.removeChild(lastChild);
        }

        // Register the indicator import with the imports manager
        if (!this.importsManager.imports.some((i) => i.alias === indicator.symbol)) {
            this.importsManager.addImports(
                IndicatorImportModel.fromIndicatorImport({ alias: indicator.symbol, ...indicator }),
            );
        }
    }

    #onChildChange() {
        if (this.rootGroup.children.length === 0) {
            this.rootGroup.addNewConditionGroup();
        }
        this.#syncIdeaEditorSignals();
        this.#updateStoreStrategy();
        this.rerender();
    }

    #onAdapterChange() {
        const state = this.adapter.getState();
        if ((state.strategy.name ?? undefined) !== this.rootGroup.label) {
            this.rootGroup.setLabel(state.strategy.name ?? undefined);
        }
        this.#isScrollingEnabled.value = state.scrolling;
        this.#syncIdeaEditorSignals();
    }

    get rootGroup() {
        return this.#state.rootGroup;
    }

    get runnableStrategy(): Idea & { plan: IfThenResearchPlan } {
        return this.adapter.getState().strategy;
    }

    get columnTemplate(): ReadonlySignal<string | ColumnTemplate | null> {
        return this.#selectedColumnTemplate;
    }

    get splitPercentage() {
        return this.adapter.getState().splitPercentage;
    }

    set splitPercentage(value: number) {
        this.adapter.persist({ splitPercentage: value });
    }

    get table() {
        return this.resultsAreaSlice.table;
    }

    get stitching() {
        return this.resultsAreaSlice.stitching;
    }

    @reacts private set state(state: SearchAlphaWidgetModelState) {
        this.#state = state;
    }

    async saveStrategy() {
        const id = this.adapter.getState().strategy.id;
        if (!id) {
            throw new Error('Cannot save strategy without an id');
        }

        this.#updateStoreStrategy();
        const idea = (await this.#updateStoreStrategy.flush()!) satisfies Idea & { plan: IfThenResearchPlan };

        this.#lastSubmit.value = Instant.now();

        const { plan, defaultUniverseId, name, description } = idea;

        const updatedStrategy = (await putIdea(id, {
            plan,
            defaultUniverseId,
            name: name ?? '',
            description,
        })) as ConcreteIdea & {
            plan: IfThenResearchPlan;
        };

        const strategyWithImports = await this.#updateIdeaWithImports(updatedStrategy);

        this.#markAsSaved();
        this.adapter.persist({ strategy: strategyWithImports, isIdeaDirty: false });

        this.analyticsService.track({
            type: 'searchalpha_save',
            strategyId: id,
            strategyName: name ?? undefined,
            isNewStrategy: false,
        });

        invalidateIdeaRecordsQueries(this.queryClient);

        return updatedStrategy;
    }

    async saveStrategyAs(name: string) {
        this.rootGroup.setLabel(name);

        this.#updateStoreStrategy();
        const strategy = {
            ...(await this.#updateStoreStrategy.flush()!),
            name,
        } satisfies Idea & { plan: IfThenResearchPlan };

        this.#lastSubmit.value = Instant.now();

        const newStrategy = (await createIdeaWithMappedImports(strategy)) as ConcreteIdea & {
            plan: IfThenResearchPlan;
        };

        const strategyWithImports = await this.#updateIdeaWithImports(newStrategy);

        this.#markAsSaved();
        this.adapter.persist({ strategy: strategyWithImports, isIdeaDirty: false });
        this.#widgetName.value = name;

        this.analyticsService.track({
            type: 'searchalpha_save',
            strategyId: newStrategy.id!,
            strategyName: name,
            isNewStrategy: true,
        });

        invalidateIdeaRecordsQueries(this.queryClient);

        return newStrategy;
    }

    get strategyId(): string | null {
        return this.adapter.getState().strategy.id || null;
    }

    get strategyName(): string | null {
        return this.adapter.getState().strategy.name || null;
    }

    setSlowIdea(slowIdea: IdeaDTO<IdeaPlanDTO> | null | undefined): void {
        this.#slowIdeasSelector.setSlowIdea(slowIdea);
    }

    get submissionBlockers(): { errorCount: number; pendingNlpCount: number } {
        const blockers = { errorCount: 0, pendingNlpCount: 0 };
        for (const { isError, isPendingNlp } of this.#iterateSubmissionBlockers()) {
            if (isError) {
                blockers.errorCount++;
            }
            if (isPendingNlp) {
                blockers.pendingNlpCount++;
            }
        }
        return blockers;
    }

    async submit() {
        if (this.backtestMode) {
            return;
        }
        if (!this.#isSubmitBlocked) {
            this.#updateStoreStrategy();
            await this.#updateStoreStrategy.flush();
            this.#lastSubmit.value = Instant.now();

            for (const child of this.#iterateChildren(this.#state.rootGroup)) {
                child.setIsDirty(false);
            }

            this.analyticsService.track({
                type: 'searchalpha_submit',
                strategyId: this.strategyId ?? undefined,
                strategyName: this.strategyName ?? undefined,
            });
        } else {
            for (const { model } of this.#iterateSubmissionBlockers()) {
                if (model instanceof ConditionModel) {
                    // If we haven't focused a field yet, look it up in the WeakMap using key `model` and if there's an element, call `.focus()` and set hasFocused true
                    model.needsAttention = true;
                }
            }
        }
    }

    /**
     * Updates the IfThen strategy in the Redux store which is saved to workspace and run via results view
     */
    #updateStoreStrategy = debounce(async (): Promise<Idea & { plan: IfThenResearchPlan }> => {
        const imports = this.importsManager.imports.map((imp) => imp.toRef().toContractType());
        const name = this.rootGroup.label;
        const plan: IfThenResearchPlan = {
            type: 'if-then',
            root: conditionGroupModelToIfThenGroup(this.#state.rootGroup),
            imports,
        };
        const newStrategy = await this.#updateIdeaWithImports({
            ...this.adapter.getState().strategy,
            name: name ?? null,
            plan,
        });

        // Once an idea is in draft state on disk it stays in draft state until an explicit
        // save/load/new path resets it (saveStrategy, saveStrategyAs, useStrategy*, createNewStrategy,
        // loadSearch). Don't downgrade to clean just because the user hasn't touched conditions yet.
        const wasIdeaDirty = this.adapter.getState().isIdeaDirty ?? false;
        this.adapter.persist({ strategy: newStrategy, isIdeaDirty: this.hasUnsavedChanges || wasIdeaDirty });

        return newStrategy;
    }, 1000);

    async #updateIdeaWithImports(
        idea: Idea & { plan: IfThenResearchPlan },
    ): Promise<Idea & { plan: IfThenResearchPlan }> {
        const indicators = await Promise.all(
            idea.plan.imports.map((ref) => this.queryClient.fetchQuery(getIndicatorFromRefQuery(ref))),
        );

        const strategyWithImports: Idea & { plan: IfThenResearchPlan } = {
            ...idea,
            plan: {
                ...idea.plan,
                imports: indicators.map((indicator) => ({
                    ...indicator,
                    alias: idea.plan.imports.find((ref) => ref.id === indicator.id)!.alias,
                })),
            },
        };

        return strategyWithImports;
    }

    #toSearchAlpha(ideaInput: ConcreteIdea): Idea & { plan: IfThenResearchPlan } {
        if (ideaInput.plan.type === 'if-then' || ideaInput.plan.type === undefined) {
            return ideaInput as unknown as Idea & { plan: IfThenResearchPlan };
        }
        throw new Error('Tried to load invalid idea type into SearchAlpha widget');
    }

    /**
     * Loads a saved search and incorporates it into the current search
     * as a child of the ConditionGroupModel passed in
     *
     * @param id The id of the saved search
     * @param parentGroup The parent group to load the saved search into
     */
    async loadSavedSearch(id: string, parentGroup: ConditionGroupModel) {
        //sanity check, ensure the parent group is a current child of the root group
        if (!this.#state.rootGroup.children.includes(parentGroup)) {
            throw new Error('Parent group is not a child of the root group');
        }

        const apiIdea = await this.queryClient.fetchQuery(getIdeaByIdQuery(id));
        if (!apiIdea) {
            throw new Error(`Idea ${id} not found`);
        }
        const idea = this.#toSearchAlpha(apiIdea);

        if (TRACE) {
            this.#log.trace({ message: 'Loaded search', idea });
        }

        const { root } = idea.plan as IfThenResearchPlan;

        function extractBaseGroup(group: IfThenGroup): IfThenGroup {
            // is there more than one lines, then return group
            if (group.lines.length > 1) {
                return group;
            }

            // is the one line is not a group, then return the group
            if (group.lines[0].type !== 'group') {
                return group;
            }

            return extractBaseGroup(group.lines[0]);
        }

        const loadedConditionGroupModel = conditionGroupModelFromIfThenGroup(extractBaseGroup(root), parentGroup);
        loadedConditionGroupModel.setLabel(idea.name ?? 'Saved Search');

        if (TRACE) {
            this.#log.trace({ message: 'Loaded condition group model', loadedConditionGroupModel });
        }

        await this.importsManager.addImportsFromRefs(
            idea.plan.imports.map((ref) => IndicatorImportRefModel.fromIndicatorImportRef(ref)),
        );

        parentGroup.addChild(loadedConditionGroupModel);

        this.analyticsService.track({
            type: 'searchalpha_load',
            strategyId: id,
            strategyName: idea.name ?? undefined,
            loadType: 'saved_search',
        });

        if (!this.#isSubmitBlocked) {
            await this.submit();
        }
    }

    async useStrategyById(id: string) {
        const apiIdea = await this.queryClient.fetchQuery(getIdeaByIdQuery(id));
        if (!apiIdea) {
            throw new Error(`Idea ${id} not found`);
        }
        const idea = this.#toSearchAlpha(apiIdea);

        let defaultUniverseId: string | null | undefined;
        try {
            const config = await this.queryClient.fetchQuery(getWorkspaceConfigQuery());
            defaultUniverseId = config.defaultUniverseId;
        } catch (_e) {
            // Do nothing
        }

        const universeId = idea.defaultUniverseId ?? defaultUniverseId ?? null;

        this.importsManager.clear();
        await this.importsManager.addImportsFromRefs(
            idea.plan.imports.map((ref) => IndicatorImportRefModel.fromIndicatorImportRef(ref)),
        );

        await this.#state.rootGroup.deferChanges(async () => {
            for (const child of this.#state.rootGroup.children) {
                this.#state.rootGroup.removeChild(child);
            }

            conditionGroupModelFromIfThenGroup(idea.plan.root, this.#state.rootGroup, true);

            this.adapter.persist({ strategy: idea, universeId, isIdeaDirty: false });

            for (const child of this.#iterateChildren(this.#state.rootGroup)) {
                child.setIsDirty(false);
                child.setIsUnsaved(false);
            }
            this.#syncIdeaEditorSignals();
        });

        this.#widgetName.value =
            idea.name ||
            (!this.#isStandaloneMode
                ? this.#widgetTabSlice.eventBus.getSignal('widget::name').value
                : t('widgets::searchalpha::name'));

        this.analyticsService.track({
            type: 'searchalpha_load',
            strategyId: id,
            strategyName: idea.name ?? undefined,
            loadType: 'idea',
        });

        if (!this.#isSubmitBlocked) {
            await this.submit();
        }
    }

    #updateFastIdeasOnSlice() {
        const strategy = this.adapter.getState().strategy;
        const tempIdea = SearchPlanDTO.fromPlanAndImports(strategy.plan, this.importsManager.imports).toTempIdea({
            name: strategy.name,
        });
        this.resultsAreaSlice.setFastIdeas([tempIdea]);
    }

    #initTotalCount() {
        this.totalTableModel.init({
            queryOptions: getTotalRowsCountQuery(this.adapter.getState().universeId ?? undefined),
            devtoolsName: 'SearchAlpha/totalCount',
        });

        this.disposableStack.use(
            this.totalTableModel.count$.subscribe((count) => {
                this.#rowCountTotalSignal.value = count;
            }),
        );

        // Reinit total count when universeId changes
        this.disposableStack.defer(
            this.adapter.subscribe(() => {
                const universeId = this.adapter.getState().universeId;
                this.totalTableModel.updateQueryOptions(getTotalRowsCountQuery(universeId ?? undefined));
            }),
        );
    }

    async #exportCSV(): Promise<void> {
        const columnTemplate = this.resultsAreaSlice.columnTemplate.value;
        if (!columnTemplate) {
            return;
        }

        const validationColumns = this.resultsAreaSlice.eventBus.getSignal('widget::validationColumns').value;

        const columnNames = [
            ...columnTemplate.columns.map((col) => ({
                id: col.id,
                name: col.name,
            })),
            ...validationColumns.map((column) => ({
                id: column.id,
                name: getValidationColumnDisplayName(column),
            })),
        ].reduce(
            (names, { id, name }) => {
                names[id] = name;
                return names;
            },
            {} as Record<string, string>,
        );

        await this.resultsAreaSlice.exportCSV(columnNames);
    }
}
